import { FullWidthBox } from '@app/components/base/FullWidthBox';
import { VideoJsPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { useLowerthirdEnabled } from '@app/modules/audit/hooks/useLowerthirdEnabled';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { isTranscriptionEnabled } from '@app/modules/logger/screens/stream/diarization/isTranscriptionEnabled';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Box } from '@mui/material';
import { useContext } from 'react';
import { useActiveStation } from '../../../../../../core/stations/hooks/useActiveStation';
import { AuditFocusMarkers } from '../../components/audit/focus-markers/AuditFocusMarkers';
import { LowerthirdFocus } from '../../components/audit/lowerthird/LowerthirdFocus';
import { AuditPreviewMarkers } from '../../components/audit/preview-markers/AuditPreviewMarkers';
import { StreamAuditResults } from '../../components/audit/results/StreamAuditResults';
import { ScreenbugFocus } from '../../components/audit/screenbug/ScreenbugFocus';
import { YoutubeStatsWaveformGraph } from '../../components/audit/youtube-stats/YoutubeStatsWaveformGraph';
import { Focus } from '../../components/focus/Focus';
import { FocusTracks } from '../../components/focus/FocusTracks';
import { StreamPlaybackHotkeysHandler } from '../../components/StreamPlaybackHotkeysHandler';
import { DiarizationContext } from '../../diarization/DiarizationContext';
import { FocusEditorChunks } from '../../fragment-editor/components/focus/FocusEditorChunks';
import { EditorView } from '../../fragment-editor/EditorView';
import { useStreamSegmentsRange } from '../../hooks/segments/useStreamSegmentsQuery';
import { useStreamSettingsStore } from '../../stores/stream-screen-settings.store';
import { StreamMode } from '../../types';
import { HoveredDiarization } from './diarization/HoveredDiarization';
import { useLoggerVideoSrc } from './hooks/useLoggerVideoSrc';
import { LoggerVideoPreview } from './LoggerVideoPreview';
import { LoggerPlayer } from './player/LoggerPlayer';
import { SyncCursorFromPlayback } from './SyncCursorFromPlayback';
import { HoveredThumbnail } from './thumbnails/HoveredThumbnail';

export function LoggerVideoMode() {
  const mode = useStreamSettingsStore((state) => state.mode);
  const editorModeEnabled = mode === StreamMode.EDITOR;
  const settingsAudit = useStreamSettingsStore((state) => state.audit);

  const station = useActiveStation();
  const transcriptionEnabled = isTranscriptionEnabled(station);
  const isLowerthirdActive = useLowerthirdEnabled(station);
  const isScreenbugActive = useFeatureIsOn('app__logger_screenbug');
  const isYtstatsActive = useYoutubeStatsEnabled(station);
  const { rawData } = useContext(DiarizationContext);

  const range = useStreamSegmentsRange();
  const src = useLoggerVideoSrc();

  return (
    <VideoJsPlaybackProvider playbackId={'logger'} src={src} playbackRange={range}>
      <SyncCursorFromPlayback />
      <StreamPlaybackHotkeysHandler />

      <LoggerPlayer />

      <FullWidthBox>
        <AuditPreviewMarkers />
        <LoggerVideoPreview />
        <Focus>
          {settingsAudit.lowerthird && isLowerthirdActive && <LowerthirdFocus />}
          <AuditFocusMarkers />
          <HoveredThumbnail />
          {transcriptionEnabled && <HoveredDiarization diarization={rawData} />}
          {settingsAudit.screenbug && isScreenbugActive && <ScreenbugFocus />}
          {settingsAudit.ytstats && isYtstatsActive && <YoutubeStatsWaveformGraph />}
          {editorModeEnabled && <FocusEditorChunks />}
        </Focus>

        <Box my={1} sx={{ position: 'relative', zIndex: 1 }}>
          <FocusTracks />
        </Box>
      </FullWidthBox>

      <Box mt={4}>{editorModeEnabled ? <EditorView /> : <StreamAuditResults />}</Box>
    </VideoJsPlaybackProvider>
  );
}
